import { Avatar, Divider, IconButton, List, ListItem, ListItemAvatar, ListItemButton, ListItemText, Typography } from "@mui/material";
import clsx from "clsx";
import { Fragment, useCallback, useMemo } from "react";
import { sortBy } from "lodash";
import { useCompanyCore } from "../../../../core/modules/company";
import { useTranslation } from "react-i18next";
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { SEARCH_USER_DIALOG } from "../../../../core/constants/dialog";
import { useGlobalCore } from "../../../../core/modules/global";
import { useNavigationCore } from "../../../../core/router/navigation";
import Swal from "sweetalert2";
import { User } from "../../../../core/interfaces/user.interface";
import CompanyRoleChip from "../../CompanyRoleChip";
import { CompanyUser } from "../../../../core/interfaces/companyUser.interface";

const UsersTab = () => {
  const { t } = useTranslation();
  const { company, users, getUsers, deleteUser } = useCompanyCore();
  const { openDialog } = useGlobalCore();
  const { goToUser } = useNavigationCore();
  
  const groupUsers = useMemo(
    () =>
      sortBy(users, ['user.full_name']).reduce(
        (prev: Record<string, CompanyUser[]>, curr) => {
          const letter = curr.user.full_name[0].toUpperCase();
          if (prev[letter]) {
            prev[letter] = [...prev[letter], curr];
          } else {
            prev[letter] = [curr];
          }

          return prev;
        },
        {}
      ),
    [users]
  );

  const onViewing = useCallback((id: string) => {
    goToUser(id);
  }, [goToUser]);

  const onRemoving = useCallback((user: User) => {
    Swal.fire({
      title: t('alert.sure.title'),
      text: t('alert.sure.delete.user', { user: user.full_name }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: t('alert.sure.confirm.button'),
      cancelButtonText: t('alert.sure.cancel.button'),
    }).then((result) => {
      if (result.isConfirmed) {
        deleteUser(company?.id!, user.id, () => {
          getUsers(company?.id!);
        });
      }
    });
  }, [company?.id, deleteUser, getUsers, t]);

  const openingDialog = useCallback(() => {
    openDialog(SEARCH_USER_DIALOG);
  }, [openDialog]);

  return (
    <div className="bg-white">
      <Divider />
      <List className="w-full m-0 p-0">
        <ListItemButton
          alignItems="center"
          onClick={openingDialog}
        >
          <div className="w-full flex justify-center items-center">
            <AddOutlinedIcon />
            {t('button.add')}
          </div>
        </ListItemButton>
      </List>
      {Object.entries(groupUsers).map(([key, group]) => (
        <div key={key} className="relative">
          <Divider />
          <Typography
            color="text.secondary"
            className="px-32 py-4 text-14 font-medium"
          >
            {key}
          </Typography>
          <List className="w-full m-0 p-0">
            {group.map((cUser) => (
              <div key={cUser.user.id}>
                <Divider />
                <ListItem
                  className="px-32 py-16"
                  sx={{ bgcolor: 'background.paper' }}
                  secondaryAction={
                    <>
                      <IconButton aria-label="view" color="success" onClick={() => onViewing(cUser.user.id)}>
                        <VisibilityIcon /> 
                      </IconButton>
                      <IconButton aria-label="delete" color="error" onClick={() => onRemoving(cUser.user)}>
                        <DeleteOutlineIcon />
                      </IconButton>
                    </>
                  }
                >
                  <ListItemAvatar>
                    {cUser.user.avatar ? (
                      <Avatar alt="contact photo" src={cUser.user.avatar} />
                    ) : (
                      <Avatar>
                        {cUser.user.full_name[0]}
                      </Avatar>
                    )}
                  </ListItemAvatar>
                  <ListItemText
                    classes={{
                      root: 'm-0',
                      primary: clsx(
                        'font-medium leading-5 truncate',
                        cUser.user.active ? 'text-slate-950' : 'text-red-700'
                      ),
                    }}
                    primary={cUser.user.full_name}
                    secondary={
                      <Fragment>
                        <Typography
                          component="div"
                          variant="body2"
                          color="text.primary"
                        >
                          {cUser.user.email}
                        </Typography>
                        <CompanyRoleChip role={cUser.role} />
                      </Fragment>
                    }
                  />
                </ListItem>
              </div>
            ))}
          </List>
        </div>
      ))}
      <Divider />
    </div>
  );
};

export default UsersTab;
